import React, { useState, useEffect, useRef } from 'react';

export default function Gallery({ images, title }) {
  const [activeIndex, setActiveIndex] = useState(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  // Handle keyboard navigation in lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (activeIndex === null) return;
      if (e.key === 'Escape') setActiveIndex(null);
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'ArrowLeft') handlePrev();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeIndex]);

  const handleNext = () => {
    if (activeIndex === null) return;
    setActiveIndex((prev) => (prev + 1) % images.length);
  };

  const handlePrev = () => {
    if (activeIndex === null) return;
    setActiveIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  // Touch swipe handlers for mobile
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    const threshold = 50; // minimum distance to trigger swipe
    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        handleNext();
      } else {
        handlePrev();
      }
    }
  };

  if (!images || images.length === 0) return null;

  return (
    <div className="space-y-6">
      {title && (
        <h3 className="font-display text-2xl text-brand-dark text-center mb-8 tracking-wide">
          {title}
        </h3>
      )}

      {/* Image Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {images.map((img, idx) => (
          <button
            key={idx}
            onClick={() => setActiveIndex(idx)}
            className="group relative aspect-square rounded-2xl overflow-hidden border border-brand-sand bg-brand-sand focus:outline-none focus:ring-2 focus:ring-brand-green/50 transition-all duration-300"
          >
            <img
              src={`https://assets.auxodev.dk/cdn-cgi/image/width=400,height=400,fit=cover,quality=80,format=auto/${img.replace('https://assets.auxodev.dk/', '')}`}
              alt={`${title || 'Galleri'} - Billede ${idx + 1}`}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
            {/* Hover Overlay */}
            <div className="absolute inset-0 bg-brand-dark/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
              <span className="w-10 h-10 rounded-full bg-brand-cream/90 flex items-center justify-center text-brand-dark shadow-md transform scale-90 group-hover:scale-100 transition-transform duration-300">
                <i className="fa-light fa-magnifying-glass-plus text-lg" />
              </span>
            </div>
          </button>
        ))}
      </div>

      {/* Lightbox Modal */}
      {activeIndex !== null && (
        <div 
          className="fixed inset-0 z-50 bg-brand-dark/95 backdrop-blur-sm flex items-center justify-center select-none"
          onClick={() => setActiveIndex(null)}
        >
          {/* Close Button */}
          <button
            onClick={() => setActiveIndex(null)}
            className="absolute top-6 right-6 text-brand-cream/80 hover:text-brand-cream p-3 transition-colors duration-300 focus:outline-none z-50"
            aria-label="Luk visning"
          >
            <i className="fa-light fa-xmark text-3xl" />
          </button>

          {/* Prev Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handlePrev();
            }}
            className="absolute left-4 sm:left-8 text-brand-cream/80 hover:text-brand-cream p-3 transition-colors duration-300 focus:outline-none z-50"
            aria-label="Forrige billede"
          >
            <i className="fa-light fa-chevron-left text-3xl sm:text-4xl" />
          </button>

          {/* Next Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleNext();
            }}
            className="absolute right-4 sm:right-8 text-brand-cream/80 hover:text-brand-cream p-3 transition-colors duration-300 focus:outline-none z-50"
            aria-label="Næste billede"
          >
            <i className="fa-light fa-chevron-right text-3xl sm:text-4xl" />
          </button>

          {/* Image Container */}
          <div 
            className="relative max-w-[90vw] max-h-[80vh] flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleZero => {
              // Simple swipe detection
              const touchDiff = (window.innerWidth / 2) - (window.innerWidth / 2); // placeholder
            }}
          >
            <img
              src={`https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=90,format=auto/${images[activeIndex].replace('https://assets.auxodev.dk/', '')}`}
              alt={`${title || 'Galleri'} - Billede ${activeIndex + 1}`}
              className="max-w-full max-h-[80vh] object-contain rounded-xl shadow-2xl"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            />
            
            {/* Counter */}
            <div className="absolute -bottom-10 text-brand-cream/80 text-sm font-sans">
              {activeIndex + 1} / {images.length}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
